import { msg } from "@lingui/core/macro";
import { createEffect, createMemo, createSignal, For, Show } from "solid-js";
import { useNavigate, useSearch } from "@tanstack/solid-router";
import { Loader2, Plus, Search, Type } from "@fleet/icons";
import { Button } from "@/components/ui/button";
import { Card, CardContent, Skeleton, TextField } from "@fleet/ui";
import { QueryErrorState } from "@/components/ui/query-error-state";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import { type AdminFontRow, type components, getAdminFontStats } from "@time-tracker/shared";
import { showToast } from "@/components/ui/toast";
import { Tabs, TabsList, TabsPanel, TabsPanels, TabsTrigger } from "@/components/ui/tabs";
import { useI18n } from "@/providers/i18n-provider";
import { FontCard } from "./font-card";
import { FontRegistryPanel } from "./font-registry-panel";

type FontTab = "approved" | "available";

export function FontManager() {
  const { t, tDynamic } = useI18n();

  // Deep-link: font search/filter travels in `?q=`. URL→signal on mount;
  // signal→URL on typing (replace); effect mirrors back/forward and pasted links.
  const urlSearch = useSearch({ strict: false });
  const navigate = useNavigate();

  const urlQ = (): string => {
    const v = (urlSearch() as { q?: unknown }).q;
    return typeof v === "string" ? v : "";
  };

  const [search, setSearchSignal] = createSignal(urlQ());
  const [isIngesting, setIsIngesting] = createSignal(false);

  // Active tab travels in `?tab=` so the URL is copyable/shareable (#15b). The
  // catalog ("approved") is the default; "available" browses the Fontsource
  // registry to approve new fonts.
  const urlTab = (): FontTab => {
    const v = (urlSearch() as Record<string, unknown>).tab;
    return v === "available" ? "available" : "approved";
  };
  const setTab = (value: string) => {
    const tab = value === "available" ? "available" : "approved";
    void navigate({
      to: ".",
      search: (prev) => ({ ...prev, tab: tab === "approved" ? undefined : tab }),
      replace: true,
    });
  };

  const setSearch = (value: string) => {
    setSearchSignal(value);
    const q = value.trim();
    void navigate({
      to: ".",
      search: (prev) => ({ ...prev, q: q === "" ? undefined : q }),
      replace: true,
    });
  };

  createEffect(() => {
    const fromUrl = urlQ();
    if (fromUrl !== search()) {
      setSearchSignal(fromUrl);
    }
  });

  const fontsQuery = createApiQuery<AdminFontRow[], components["schemas"]["ErrorModel"]>(
    () => ["admin", "fonts"],
    async () => api.GET("/api/admin/font/listAll", {}),
    () => ({ staleTime: 30_000 }),
  );

  const toggleMutation = createApiMutation(async (vars: { fontId: string; isActive: boolean }) =>
    api.POST("/api/admin/font/toggleActive", { body: vars }),
  );

  const deleteMutation = createApiMutation(async (vars: { fontId: string }) =>
    api.POST("/api/admin/font/delete", { body: vars }),
  );

  const ingestMutation = createApiMutation(
    async (vars: {
      family: string;
      category: string;
      weights: string[];
      sortOrder?: number;
      forceReIngest?: boolean;
    }) => api.POST("/api/admin/font/ingest", { body: vars }),
  );

  const filteredFonts = createMemo(() => {
    const s = search().toLowerCase();
    const data = fontsQuery.data ?? [];
    return data.filter(
      (f) => f.family.toLowerCase().includes(s) || f.name.toLowerCase().includes(s),
    );
  });

  const stats = createMemo(() => getAdminFontStats(fontsQuery.data ?? []));

  const handleIngest = async (e: Event) => {
    e.preventDefault();
    const family = search().trim();
    if (!family) {
      return;
    }

    setIsIngesting(true);
    try {
      await ingestMutation.mutateAsync({
        family,
        category: "sans-serif" as const,
        weights: ["400"] as const,
      });
      await fontsQuery.refetch();
      setSearch("");
      showToast(t(msg`Font "${family}" ingested successfully`), "success");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      showToast(t(msg`Error: ${message}`), "error");
    } finally {
      setIsIngesting(false);
    }
  };

  return (
    <div class="space-y-8">
      <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div class="flex items-center gap-3">
          <Type class="h-8 w-8 text-primary" />
          <div>
            <h2 class="text-3xl font-bold tracking-tight">{t(msg`Font Catalog`)}</h2>
            <p class="text-sm text-muted-foreground">
              {/* oxlint-disable-next-line lingui/no-expression-in-message */}
              {t(msg`${stats().total} fonts total • ${stats().active} active`)}
            </p>
          </div>
        </div>
      </div>

      <Tabs value={urlTab()} onChange={setTab}>
        <TabsList>
          <TabsTrigger value="approved">{t(msg`Approved`)}</TabsTrigger>
          <TabsTrigger value="available">{t(msg`Available`)}</TabsTrigger>
        </TabsList>

        <TabsPanels value={urlTab()} order={["approved", "available"]}>
          <TabsPanel value="approved">
            <div class="space-y-8 pt-4">
              <Card class="bg-primary/5 border-primary/10">
                <CardContent class="p-4">
                  <form onSubmit={handleIngest} class="flex gap-2">
                    <div class="relative flex-1">
                      <Search class="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <TextField
                        placeholder={t(msg`Google font name (e.g., Roboto, Inter...)`)}
                        class="pl-9"
                        value={search()}
                        onInput={(e) => setSearch(e.currentTarget.value)}
                      />
                    </div>
                    <Button type="submit" disabled={isIngesting() || !search()}>
                      <Show
                        when={isIngesting()}
                        fallback={
                          <>
                            <Plus class="mr-2 h-4 w-4" /> {t(msg`Ingest`)}
                          </>
                        }
                      >
                        <Loader2 class="mr-2 h-4 w-4 animate-spin" /> {t(msg`Ingesting...`)}
                      </Show>
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* P23 / CC-60 - I3: <<no se pudo cargar>> NO es <<no hay nada>>. Con el API
                  caido este panel pintaba su estado VACIO como si el dato fuera real. El
                  bloque de contenido se ENVUELVE en lugar de encadenar `&& !fontsQuery.isError`
                  en el guard existente: en error `isLoading` es `false`, asi que el guard
                  pintaria su esqueleto Y la caja de error a la vez, para siempre. */}
              <Show when={fontsQuery.isError}>
                <QueryErrorState
                  testId="admin-fonts-error"
                  compact
                  onRetry={() => void fontsQuery.refetch()}
                />
              </Show>
              <Show when={!fontsQuery.isError}>
                <Show
                  when={!fontsQuery.isLoading}
                  fallback={
                    <div class="grid gap-4 md:grid-cols-2">
                      <For each={Array.from({ length: 4 }, () => 0)}>
                        {() => <Skeleton class="h-32 w-full rounded-xl" />}
                      </For>
                    </div>
                  }
                >
                  <div class="grid gap-4 md:grid-cols-2">
                    <For each={filteredFonts()}>
                      {(font) => (
                        <FontCard
                          font={font}
                          onToggleActive={(id, active) => {
                            toggleMutation.mutate(
                              { fontId: id, isActive: active },
                              {
                                onSuccess: () => {
                                  void fontsQuery.refetch();
                                },
                              },
                            );
                          }}
                          onDeleteConfirm={(id) => {
                            const fontFamily = font.family;
                            deleteMutation.mutate(
                              { fontId: id },
                              {
                                onSuccess: () => {
                                  void fontsQuery.refetch();
                                  showToast(t(msg`Font "${fontFamily}" deleted`), "success");
                                },
                              },
                            );
                          }}
                          tDynamic={tDynamic}
                        />
                      )}
                    </For>
                  </div>
                </Show>
              </Show>
            </div>
          </TabsPanel>

          <TabsPanel value="available">
            <div class="pt-4">
              <FontRegistryPanel />
            </div>
          </TabsPanel>
        </TabsPanels>
      </Tabs>
    </div>
  );
}
